<template>
  <v-dialog v-model="dialogValue" persistent max-width="600">
    <v-card>
      <v-card-title class="text-primary text-h6 font-weight-bold pa-6 pb-2">
        Edit Digital Twin
      </v-card-title>

      <v-card-text class="pa-6 pt-4">
        <v-row>
          <v-col cols="12" sm="4">
            <v-text-field
              v-model.number="form.displayOrder"
              label="Display Order"
              type="number"
              variant="outlined"
              density="compact"
              color="primary"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12">
            <v-file-input
              v-model="baseLayoutFile"
              label="Base Layout"
              accept=".svg"
              prepend-icon="mdi-image-outline"
              variant="outlined"
              density="compact"
              color="primary"
              hide-details="auto"
              :hint="
                isCreatingLayout
                  ? 'SVG only. A background is required to set up this Digital Twin.'
                  : 'SVG only. Leave empty to keep the current layout'
              "
              persistent-hint
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pa-6 pt-0">
        <v-spacer />
        <v-btn
          variant="text"
          color="primary"
          :disabled="saving"
          @click="cancel"
        >
          Cancel
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :loading="saving"
          :disabled="isCreatingLayout && !selectedFile"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { IDigitalTwin } from '../types';
import * as API from '../API';
import { BLANK_BASE_LAYOUT, blankBaseLayoutFile } from '../blankBaseLayout';

const props = defineProps<{
  modelValue: boolean;
  siteId?: number;
  digitalTwin?: IDigitalTwin | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  saved: [siteId?: number];
}>();

const dialogValue = computed({
  get: () => props.modelValue,
  set: (val: boolean) => emit('update:modelValue', val),
});

const form = ref<{ displayOrder: number }>({
  displayOrder: 1,
});

/** The SVG file selected by the user. Sent as-is via FormData (multipart). */
const baseLayoutFile = ref<File | File[] | null>(null);
const saving = ref(false);

const selectedFile = computed<File | null>(() => {
  if (!baseLayoutFile.value) return null;
  return Array.isArray(baseLayoutFile.value)
    ? (baseLayoutFile.value[0] ?? null)
    : baseLayoutFile.value;
});

/**
 * True when the twin has no base layout at all. Placeholders already carry a
 * blank SVG, so a file is only required if that default is missing.
 */
const isCreatingLayout = computed<boolean>(() => !props.digitalTwin?.baseLayout);

watch(
  () => props.digitalTwin,
  (twin) => {
    if (twin) {
      form.value.displayOrder = twin.displayOrder;
    }
  },
  { immediate: true }
);

/** Reset form when dialog opens */
watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      form.value = { displayOrder: props.digitalTwin?.displayOrder ?? 1 };
      baseLayoutFile.value = null;
    }
  }
);

function cancel(): void {
  dialogValue.value = false;
}

async function save(): Promise<void> {
  if (!props.digitalTwin) return;
  saving.value = true;
  try {
    const keepBlankLayout =
      !selectedFile.value &&
      props.digitalTwin.baseLayout === BLANK_BASE_LAYOUT;
    await API.updateDigitalTwin(props.digitalTwin.siteSystemId, {
      baseLayout:
        selectedFile.value ??
        (keepBlankLayout ? blankBaseLayoutFile() : undefined),
      overlay: JSON.stringify({ items: props.digitalTwin.overlay, segments: props.digitalTwin.segments }),
      displayOrder: form.value.displayOrder,
    });
    emit('saved', props.siteId);
    dialogValue.value = false;
  } finally {
    saving.value = false;
  }
}
</script>
